import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LayoutDashboard } from "lucide-react";
import EarlyAccessCta from "./EarlyAccessCta";
import { LOGIN_URL, APP_URL, PRODUCT_LIVE } from "@/lib/api";
import { useSession } from "@/lib/session";
import Logo from "./Logo";

const navLinks = [
    { label: "Features", href: "/features" },
    { label: "Demo", href: "/demo" },
    { label: "Pricing", href: "/pricing" },
    { label: "ROI", href: "/#roi-calculator" },
    { label: "FAQ", href: "/#faq" },
];

export default function Navbar({ tone = "dark" }) {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { pathname, hash } = useLocation();
    const navigate = useNavigate();
    const { loggedIn } = useSession();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // When the URL has a hash (e.g. /#faq), scroll the matching element into
    // view. Runs on every pathname/hash change so cross-page nav (Pricing → /#roi)
    // works after the Landing page mounts.
    useEffect(() => {
        if (!hash) return;
        const id = hash.replace(/^#/, "");
        const tid = setTimeout(() => {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 60);
        return () => clearTimeout(tid);
    }, [pathname, hash]);

    /** Smooth-scroll to an in-page anchor when the user is already on that
     * page; otherwise fall through to React Router navigation, which the
     * effect above will pick up after mount. Closes mobile drawer too. */
    const handleNavClick = (href) => (e) => {
        setOpen(false);
        if (!href.includes("#")) return;
        const [path, anchor] = href.split("#");
        const targetPath = path || "/";
        if (pathname === targetPath) {
            e.preventDefault();
            const el = document.getElementById(anchor);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            navigate(`${targetPath}#${anchor}`, { replace: true });
        }
    };

    const isDark = tone === "dark";
    const baseClass = isDark
        ? `${scrolled ? "bg-zinc-950/80 border-zinc-800" : "bg-transparent border-transparent"} text-white`
        : `${scrolled ? "bg-white/85 border-zinc-200" : "bg-white/60 border-transparent"} text-zinc-900`;

    return (
        <header
            data-testid="site-navbar"
            className={`sticky top-0 z-50 w-full border-b backdrop-blur-xl transition-colors ${baseClass}`}
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
                <Logo variant={isDark ? "dark" : "light"} />

                <nav className="hidden md:flex items-center gap-8">
                    {navLinks.map((link) => (
                        <Link
                            key={link.href}
                            to={link.href}
                            onClick={handleNavClick(link.href)}
                            data-testid={`nav-link-${link.label.toLowerCase()}`}
                            className={`text-sm font-medium transition-colors ${
                                pathname === link.href
                                    ? isDark ? "text-white" : "text-zinc-900"
                                    : isDark ? "text-zinc-400 hover:text-white" : "text-zinc-600 hover:text-zinc-900"
                            }`}
                        >
                            {link.label}
                        </Link>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    {PRODUCT_LIVE && !loggedIn && (
                        <a
                            href={LOGIN_URL}
                            data-testid="nav-signin-link"
                            className={`text-sm font-medium ${isDark ? "text-zinc-300 hover:text-white" : "text-zinc-600 hover:text-zinc-900"}`}
                        >
                            Sign in
                        </a>
                    )}
                    {PRODUCT_LIVE && loggedIn ? (
                        <a
                            href={APP_URL}
                            data-testid="nav-dashboard-btn"
                            className="inline-flex items-center gap-1.5 bg-brand-primary hover:bg-brand-hover text-white rounded-lg h-9 px-4 text-sm font-medium transition-colors"
                        >
                            <LayoutDashboard className="h-4 w-4" />
                            Dashboard
                        </a>
                    ) : (
                        <EarlyAccessCta
                            data-testid="nav-start-free-btn"
                            className="bg-brand-primary hover:bg-brand-hover text-white rounded-lg h-9 px-4 font-medium"
                        />
                    )}
                </div>

                <button
                    data-testid="nav-mobile-toggle"
                    className="md:hidden p-2"
                    onClick={() => setOpen(!open)}
                    aria-label="Toggle menu"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            {open && (
                <div className={`md:hidden border-t ${isDark ? "bg-zinc-950 border-zinc-800" : "bg-white border-zinc-200"}`}>
                    <div className="px-6 py-4 flex flex-col gap-3">
                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                to={link.href}
                                onClick={handleNavClick(link.href)}
                                data-testid={`nav-mobile-${link.label.toLowerCase()}`}
                                className={`text-base font-medium py-2 ${isDark ? "text-zinc-200" : "text-zinc-800"}`}
                            >
                                {link.label}
                            </Link>
                        ))}
                        <a href={LOGIN_URL} className={`text-base font-medium py-2 ${isDark ? "text-zinc-200" : "text-zinc-800"} ${PRODUCT_LIVE && !loggedIn ? "" : "hidden"}`}>
                            Sign in
                        </a>
                        {PRODUCT_LIVE && loggedIn ? (
                            <a
                                href={APP_URL}
                                data-testid="nav-mobile-dashboard"
                                className="inline-flex items-center justify-center gap-1.5 bg-brand-primary hover:bg-brand-hover text-white rounded-lg h-10 px-4 font-medium w-full"
                            >
                                <LayoutDashboard className="h-4 w-4" />
                                Go to dashboard
                            </a>
                        ) : (
                            <EarlyAccessCta
                                data-testid="nav-mobile-start-free"
                                className="bg-brand-primary hover:bg-brand-hover text-white rounded-lg h-10 px-4 font-medium flex items-center justify-center w-full"
                            />
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
